import React, { useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface SearchableOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  clearable?: boolean;
  id?: string;
  name?: string;
  className?: string;
  inputClassName?: string;
  required?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled = false,
  isLoading = false,
  emptyMessage = 'No options found',
  clearable = true,
  id,
  name,
  className = '',
  inputClassName = '',
  required = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-required': ariaRequired,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listboxId = `${inputId}-listbox`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const searchTerm = query.trim().toLowerCase();
    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => (
      option.label.toLowerCase().includes(searchTerm) ||
      option.description?.toLowerCase().includes(searchTerm) ||
      option.id.toLowerCase().includes(searchTerm)
    ));
  }, [options, query]);

  const close = () => {
    setIsOpen(false);
    setQuery('');
  };

  const selectOption = (option: SearchableOption) => {
    if (option.disabled) {
      return;
    }
    onChange(option.id);
    close();
  };

  const isDisabled = disabled || isLoading;
  const displayValue = isOpen ? query : selectedOption?.label ?? '';

  return (
    <div
      ref={wrapperRef}
      className={`relative ${className}`}
      onBlur={(event) => {
        if (!wrapperRef.current?.contains(event.relatedTarget as Node | null)) {
          close();
        }
      }}
    >
      {name ? <input type="hidden" name={name} value={value} required={required} /> : null}
      <div
        className={`relative w-full overflow-hidden rounded-lg border border-border-default bg-surface-base shadow-sm transition-all focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 ${
          isDisabled ? 'bg-surface-raised' : ''
        }`}
      >
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-required={ariaRequired}
          className={`w-full border-none bg-transparent py-2 pl-9 pr-16 text-sm leading-5 text-content-primary placeholder:text-content-muted focus:outline-none disabled:cursor-not-allowed ${inputClassName}`}
          placeholder={selectedOption ? 'Search options...' : placeholder}
          value={displayValue}
          disabled={isDisabled}
          onFocus={() => {
            setIsOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              close();
            }
          }}
        />

        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
          {clearable && value && !isLoading ? (
            <button
              type="button"
              onClick={() => {
                onChange('');
                close();
              }}
              className="rounded p-1 text-content-muted transition-colors hover:bg-surface-raised hover:text-content-primary"
              disabled={disabled}
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          ) : null}
          <button
            type="button"
            className="rounded p-1 text-content-muted transition-colors hover:bg-surface-raised hover:text-content-primary disabled:cursor-not-allowed"
            disabled={isDisabled}
            onClick={() => {
              setIsOpen((current) => !current);
              setQuery('');
            }}
            aria-label={isOpen ? 'Close options' : 'Open options'}
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {isOpen ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border-default bg-surface-base py-1 shadow-lg"
        >
          {filteredOptions.length === 0 ? (
            <div className="relative cursor-default select-none px-4 py-2 text-sm text-content-muted">
              {isLoading ? 'Loading...' : emptyMessage}
            </div>
          ) : (
            filteredOptions.map((option) => {
              const selected = option.id === value;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={option.disabled}
                  className={`flex w-full cursor-pointer items-start justify-between gap-2 px-4 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    selected
                      ? 'bg-primary-50 text-primary-800 dark:text-primary-100'
                      : 'text-content-primary hover:bg-primary-500 hover:text-white'
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{option.label}</span>
                    {option.description ? (
                      <span className={`block truncate text-xs ${selected ? 'text-primary-700 dark:text-primary-200' : 'text-content-muted'}`}>
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                  {selected ? <Check className="mt-0.5 h-4 w-4 flex-shrink-0" /> : null}
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};
